import {
  addSeconds,
  differenceInCalendarDays,
  endOfDay,
  endOfMonth,
  getDaysInMonth,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  max,
  min,
  startOfDay,
  startOfMonth,
} from 'date-fns'
import { isEmpty } from 'lodash'
import * as Effects from 'redux-saga/effects'
import { select } from 'redux-saga/effects'
import { EventStatus } from '../../models/Event'
import { EventActions } from '../../reducers/bookingCalendar/actionNames'
import { withoutCancelled } from '../../reducers/bookingCalendar/selectors/events'
import { getSortedRoomsByPosition } from '../../reducers/bookingCalendar/selectors/rooms'
import { ChartsType } from '../../reducers/stats'
import { StatsActions } from '../../reducers/stats/actionNames'
import {
  getStatsChartsType,
  getStatsMonth,
  getStatsSelectedRooms,
  getStatsYear,
} from '../../reducers/stats/selectors'
import { theme } from '../../theme'
import { inDateRange } from '../../utils/GridCreator/makeGrid'

function* getStats(action) {
  if (window.location.pathname !== '/statistics/reservations') {
    return ''
  }

  const [year, month, selected, type, rooms] = yield Effects.all([
    Effects.select(getStatsYear),
    Effects.select(getStatsMonth),
    Effects.select(getStatsSelectedRooms),
    Effects.select(getStatsChartsType),
    Effects.select(getSortedRoomsByPosition),
  ])

  const allRooms = rooms.map(r => String(r.id))

  if (isEmpty(rooms)) {
    if (action.count > 10) {
      return ''
    }
    yield Effects.delay(300)
    yield* getStats({ count: action.count ? action.count + 1 : 1 })
    return ''
  }

  const events = yield select(withoutCancelled)
  const allEvents = events
    .filter(e => !e.isDeleted && e.stage !== EventStatus.Canceled)
    .filter(
      e =>
        (isBefore(e.startDate, endOfMonth(new Date(year, 11, 1))) &&
          isAfter(e.finishDate, startOfMonth(new Date(year, 0, 1)))) ||
        inDateRange(
          e.createdAt,
          startOfMonth(new Date(year, 0, 1)),
          endOfMonth(new Date(year, 11, 1)),
        ),
    )

  const months = Array.from(new Array(12))
  const days = getDaysInMonth(new Date(year, month - 1))
  let reservation = []

  if (month === 0) {
    reservation = months.map((_, i) => {
      return [
        {
          color: theme.colors.palette.emerald,
          roomId: 'BBO_907_new',
          value: allEvents.filter(e =>
            isSameMonth(e.createdAt, new Date(year, i, 1)),
          ).length,
        },
        {
          color: '#5674b9',
          roomId: 'BBO_907_starting',
          value: allEvents.filter(e =>
            isSameMonth(e.startDate, new Date(year, i, 1)),
          ).length,
        },
      ]
    })
  } else {
    reservation = Array.from(new Array(days)).map((_, i) => {
      return [
        {
          color: theme.colors.palette.emerald,
          roomId: 'BBO_907_new',
          value: allEvents.filter(e =>
            isSameDay(e.createdAt, new Date(year, month - 1, i + 1)),
          ).length,
        },
        {
          color: '#5674b9',
          roomId: 'BBO_907_starting',
          value: allEvents.filter(e =>
            isSameDay(e.startDate, new Date(year, month - 1, i + 1)),
          ).length,
        },
      ]
    })
  }

  let occupancy: any[][] = months.map(_ => selected.map(r => ({})))
  let avgPrice: any[][] = months.map(_ => selected.map(r => ({})))
  let RevPAR: any[][] = months.map(_ => selected.map(r => ({})))

  if (month === 0) {
    occupancy = months.map(_ => selected.map(r => ({})))
    avgPrice = months.map(_ => selected.map(r => ({})))
    RevPAR = months.map(_ => selected.map(r => ({})))

    months.forEach((_, i) => {
      const daysInMonth = getDaysInMonth(new Date(year, i, 1))
      ;(type === ChartsType.Selected ? selected : allRooms).forEach((r, j) => {
        const startDate = startOfMonth(new Date(year, i, 1))
        const finishDate = endOfMonth(new Date(year, i, 1))

        const events = allEvents.filter(
          e =>
            String(e.roomId) === String(r) &&
            (inDateRange(e.startDate, startDate, finishDate) ||
              inDateRange(e.finishDate, startDate, finishDate) ||
              inDateRange(startDate, e.startDate, e.finishDate) ||
              inDateRange(finishDate, e.startDate, e.finishDate)),
        )

        const eventsReservationDays = events.reduce(
          (a, b) =>
            a +
            differenceInCalendarDays(
              min(b.finishDate, addSeconds(finishDate, 1)),
              max(b.startDate, startDate),
            ),
          0,
        )

        const eventsSumedPrice = events.reduce((a, b) => {
          return (
            a +
            Number(
              b._price / differenceInCalendarDays(b.finishDate, b.startDate),
            ) *
              differenceInCalendarDays(
                min(b.finishDate, addSeconds(finishDate, 1)),
                max(b.startDate, startDate),
              )
          )
        }, 0)

        const eventOccupancy = Math.round(
          (eventsReservationDays / daysInMonth) * 100,
        )

        const eventAvgPrice = eventsReservationDays
          ? eventsSumedPrice / eventsReservationDays
          : 0

        let eventRevPAR
        try {
          eventRevPAR = eventAvgPrice * (eventOccupancy / 100)
        } catch (e) {
          eventRevPAR = 0
        }

        occupancy[i][j] = { roomId: r, value: eventOccupancy }
        avgPrice[i][j] = { roomId: r, value: eventAvgPrice }
        RevPAR[i][j] = { roomId: r, value: eventRevPAR }
      })

      if (type === ChartsType.All) {
        occupancy[i] = [
          occupancy[i].reduce(
            (a, b, i) => {
              a.value = a.value + b.value / allRooms.length
              return a
            },
            {
              color: theme.colors.palette.emerald,
              roomId: 'BBO_907_occupancy_summary',
              value: 0,
            },
          ),
        ]

        avgPrice[i] = [
          avgPrice[i].reduce(
            (a, b, i) => {
              a.value = a.value + b.value / allRooms.length
              return a
            },
            {
              color: theme.colors.palette.emerald,
              roomId: 'BBO_907_price_summary',
              value: 0,
            },
          ),
        ]

        const par =
          Math.round(avgPrice[i][0].value) *
          (Math.round(occupancy[i][0].value) / 100)

        RevPAR[i] = [
          {
            color: theme.colors.palette.emerald,
            roomId: 'BBO_907_revpar_summary',
            value: par < 1 && par > 0.4 ? 1 : par,
          },
        ]
      }
    })
  } else {
    occupancy = Array.from(new Array(days)).map(_ => selected.map(r => ({})))
    avgPrice = Array.from(new Array(days)).map(_ => selected.map(r => ({})))
    RevPAR = Array.from(new Array(days)).map(_ => selected.map(r => ({})))

    Array.from(new Array(days)).forEach((_, i) => {
      ;(type === ChartsType.Selected ? selected : allRooms).forEach(
        (r, j, a) => {
          const startDate = startOfDay(new Date(year, month - 1, i + 1))
          const finishDate = endOfDay(new Date(year, month - 1, i + 1))

          const events = allEvents.filter(e => {
            return (
              String(e.roomId) === String(r) &&
              (((isSameMonth(e.startDate, startDate) ||
                isSameMonth(e.finishDate, finishDate)) &&
                (isSameDay(e.startDate, startDate) ||
                  isSameDay(e.finishDate, finishDate))) ||
                (inDateRange(startDate, e.startDate, e.finishDate) ||
                  inDateRange(finishDate, e.startDate, e.finishDate)))
            )
          })

          const eventsSumedPrice = events.reduce((a, b) => {
            return a + Number(b._price)
          }, 0)

          const eventOccupancy = Math.round((events.length / a.length) * 100)
          const eventAvgPrice = events.length
            ? eventsSumedPrice / events.length
            : 0

          let eventRevPAR

          try {
            eventRevPAR = eventAvgPrice * (eventOccupancy / 100)
          } catch (e) {
            eventRevPAR = 0
          }

          occupancy[i][j] = { roomId: r, value: eventOccupancy }
          avgPrice[i][j] = { roomId: r, value: eventAvgPrice }
          RevPAR[i][j] = { roomId: r, value: eventRevPAR }
        },
      )

      if (type === ChartsType.All) {
        occupancy[i] = [
          occupancy[i].reduce(
            (a, b, i) => {
              a.value = a.value + b.value / allRooms.length
              return a
            },
            {
              color: theme.colors.palette.emerald,
              roomId: 'BBO_907_occupancy_summary',
              value: 0,
            },
          ),
        ]

        avgPrice[i] = [
          avgPrice[i].reduce(
            (a, b, i) => {
              a.value = a.value + b.value / allRooms.length
              return a
            },
            {
              color: theme.colors.palette.emerald,
              roomId: 'BBO_907_price_summary',
              value: 0,
            },
          ),
        ]

        const par = avgPrice[i][0].value * (occupancy[i][0].value / 100)

        RevPAR[i] = [
          {
            color: theme.colors.palette.emerald,
            roomId: 'BBO_907_revpar_summary',
            value: par < 1 && par > 0.4 ? 1 : par,
          },
        ]
      }
    })
  }

  yield Effects.put({
    RevPAR,
    avgPrice,
    occupancy,
    reservation,
    type: StatsActions.SetStats,
  })
}

export default function*() {
  yield Effects.takeLatest(
    [
      StatsActions.SetYear,
      StatsActions.SetMonth,
      StatsActions.SetSelectedRooms,
      StatsActions.SetChartsType,
      StatsActions.LoadStats,
      EventActions.DataForEventsAccepted,
    ],
    getStats,
  )
}
